import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import BackButton from '../../../components/shared/actions/BackButton';
import { UserContext } from '../../../components/shared/useUser';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import DeliverySchedulesListPageContent from './components/DeliverySchedulesListPageContent';

const DeliverySchedulesPage = () => {
  const { t } = useTranslation('odsmanagement');
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);
  const canView = hasPermissionTo(['ODSRead']);

  const { defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();
  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterOrderTypes, setCurrentFilterOrderTypes] = useState<string>('');
  const [currentFilterSupplier, setCurrentFilterSupplier] = useState<string>('');
  const [currentFilterDeliveryDateFrom, setCurrentFilterDeliveryDateFrom] = useState<string>('');
  const [currentFilterDeliveryDateTo, setCurrentFilterDeliveryDateTo] = useState<string>('');
  const [currentFilterCutOffDateFrom, setCurrentFilterCutOffDateFrom] = useState<string>('');
  const [currentFilterCutOffDateTo, setCurrentFilterCutOffDateTo] = useState<string>('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    setCurrentFilterInstance(searchParams.get('companyId') || '');
    setCurrentFilterMarket(searchParams.get('customerTypeId') || '');
    setCurrentFilterOrderTypes(searchParams.get('orderTypes') || '');
    setCurrentFilterSupplier(searchParams.get('supplierNumber') || '');
    setCurrentFilterDeliveryDateFrom(searchParams.get('deliveryDateFrom') || '');
    setCurrentFilterDeliveryDateTo(searchParams.get('deliveryDateTo') || '');
    setCurrentFilterCutOffDateFrom(searchParams.get('cutOffDateFrom') || '');
    setCurrentFilterCutOffDateTo(searchParams.get('cutOffDateTo') || '');
  }, [location.search]);

  // Auto-populate from the user's default instance/market once, matching the other Procurement list pages.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  if (!canView) {
    return null;
  }

  const handleFilterChange = (
    instance: string,
    market: string,
    filters?: {
      orderTypes?: string;
      supplierNumber?: string;
      deliveryDateFrom?: string;
      deliveryDateTo?: string;
      cutOffDateFrom?: string;
      cutOffDateTo?: string;
    },
  ) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);

    const nextOrderTypes = filters?.orderTypes ?? currentFilterOrderTypes;
    const nextSupplier = filters?.supplierNumber ?? currentFilterSupplier;
    const nextDeliveryDateFrom = filters?.deliveryDateFrom ?? currentFilterDeliveryDateFrom;
    const nextDeliveryDateTo = filters?.deliveryDateTo ?? currentFilterDeliveryDateTo;
    const nextCutOffDateFrom = filters?.cutOffDateFrom ?? currentFilterCutOffDateFrom;
    const nextCutOffDateTo = filters?.cutOffDateTo ?? currentFilterCutOffDateTo;

    if (filters) {
      setCurrentFilterOrderTypes(nextOrderTypes);
      setCurrentFilterSupplier(nextSupplier);
      setCurrentFilterDeliveryDateFrom(nextDeliveryDateFrom);
      setCurrentFilterDeliveryDateTo(nextDeliveryDateTo);
      setCurrentFilterCutOffDateFrom(nextCutOffDateFrom);
      setCurrentFilterCutOffDateTo(nextCutOffDateTo);
    }

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (nextOrderTypes) params.set('orderTypes', nextOrderTypes);
    if (nextSupplier) params.set('supplierNumber', nextSupplier);
    if (nextDeliveryDateFrom) params.set('deliveryDateFrom', nextDeliveryDateFrom);
    if (nextDeliveryDateTo) params.set('deliveryDateTo', nextDeliveryDateTo);
    if (nextCutOffDateFrom) params.set('cutOffDateFrom', nextCutOffDateFrom);
    if (nextCutOffDateTo) params.set('cutOffDateTo', nextCutOffDateTo);
    navigate(`?${params.toString()}`, { replace: true });
  };

  return (
    <DeliverySchedulesListPageContent
      onFilterChange={handleFilterChange}
      initialInstance={currentFilterInstance}
      initialMarket={currentFilterMarket}
      initialOrderTypes={currentFilterOrderTypes}
      initialSupplier={currentFilterSupplier}
      initialDeliveryDateFrom={currentFilterDeliveryDateFrom}
      initialDeliveryDateTo={currentFilterDeliveryDateTo}
      initialCutOffDateFrom={currentFilterCutOffDateFrom}
      initialCutOffDateTo={currentFilterCutOffDateTo}
    >
      {({ filterBar, quickFilterContent, advancedFilterContent, advancedFilterActions, actions, headlineContent, table, isInitialLoading }) => (
        <ListPageTemplate
          title={t('title')}
          subtitle={t('procurementListSubtitle', 'Manage delivery schedules, cutoff dates, and bulk changes.')}
          loading={isInitialLoading}
          loadingLabel={t('loading', 'Loading...')}
          backButton={(
            <BackButton
              handleClick={() => navigate('/settings')}
              title={t('backToSettings', 'Back to Settings')}
              isLink={false}
            />
          )}
          actions={actions}
          headlineContent={headlineContent}
          filterContent={filterBar}
          quickFilterContent={quickFilterContent}
          advancedFilterContent={advancedFilterContent}
          advancedFilterActions={advancedFilterActions}
          table={table}
        />
      )}
    </DeliverySchedulesListPageContent>
  );
};

export default DeliverySchedulesPage;
